/**
 * Data structure for System Component Visualizer
 * Maps heating systems to their physical components and locations
 */

export type ComponentLocation = 'Loft' | 'Airing Cupboard' | 'Utility/Kitchen' | 'External';

export interface SystemComponent {
  label: string;
  iconKey: 'boiler' | 'cylinder' | 'tank' | 'heatPump' | 'buffer';
  location: ComponentLocation;
  optional?: boolean;
}

export interface CapacityInfo {
  showers: number;
  baths: number;
  description: string;
}

export interface SystemLayout {
  systemType: string;
  components: SystemComponent[];
  capacity: CapacityInfo;
  thermalBehavior: 'gas' | 'heatPump';
}

/**
 * System layout mappings for different heating system types
 */

// Base system configurations to reduce duplication
const COMBI_BASE: SystemLayout = {
  systemType: 'Combi',
  components: [
    { label: 'Wall Boiler', iconKey: 'boiler', location: 'Utility/Kitchen' },
  ],
  capacity: {
    showers: 1,
    baths: 0,
    description: 'On-demand hot water with tap lag - one outlet at a time'
  },
  thermalBehavior: 'gas'
};

const REGULAR_BASE: SystemLayout = {
  systemType: 'Regular Vented',
  components: [
    { label: 'Boiler', iconKey: 'boiler', location: 'Utility/Kitchen' },
    { label: 'Vented Cylinder', iconKey: 'cylinder', location: 'Airing Cupboard' },
    { label: 'CWS Tank', iconKey: 'tank', location: 'Loft' },
    { label: 'F&E Tank', iconKey: 'tank', location: 'Loft' },
  ],
  capacity: {
    showers: 1,
    baths: 1,
    description: 'Stored hot water with gravity-fed pressure'
  },
  thermalBehavior: 'gas'
};

const SYSTEM_BASE: SystemLayout = {
  systemType: 'System Unvented',
  components: [
    { label: 'System Boiler', iconKey: 'boiler', location: 'Utility/Kitchen' },
    { label: 'Unvented Cylinder', iconKey: 'cylinder', location: 'Airing Cupboard' },
  ],
  capacity: {
    showers: 2,
    baths: 1,
    description: 'High-pressure stored hot water - multiple outlets'
  },
  thermalBehavior: 'gas'
};

const HEAT_PUMP_BASE: SystemLayout = {
  systemType: 'Heat Pump',
  components: [
    { label: 'ASHP Unit', iconKey: 'heatPump', location: 'External' },
    { label: 'HP Cylinder', iconKey: 'cylinder', location: 'Airing Cupboard' },
    { label: 'Buffer Tank', iconKey: 'buffer', location: 'Airing Cupboard', optional: true },
  ],
  capacity: {
    showers: 2,
    baths: 1,
    description: 'High-capacity hot water with efficient heat pump technology'
  },
  thermalBehavior: 'heatPump'
};

export const SYSTEM_LAYOUTS: Record<string, SystemLayout> = {
  // Combi configurations
  'combi': COMBI_BASE,
  
  // Regular configurations
  'regular': REGULAR_BASE,
  
  // System configurations
  'system': SYSTEM_BASE,
  'system_boiler': SYSTEM_BASE,
  
  // Heat pump configurations
  'heat_pump': HEAT_PUMP_BASE,
  'air_source_hp': HEAT_PUMP_BASE,
  'ashp': HEAT_PUMP_BASE,
};

/**
 * Get system layout for a given system type
 * 
 * This function uses a multi-stage matching strategy:
 * 1. Exact match with defined keys
 * 2. Normalized match (removing underscores, spaces, hyphens for flexible matching)
 * 3. Pattern-based fallback for common variations
 * 
 * Note: The normalization intentionally treats variations like 'heat_pump', 'heat-pump',
 * and 'heatpump' as equivalent to provide flexibility for different naming conventions
 * used across the codebase.
 */
export function getSystemLayout(systemType: string | undefined): SystemLayout | null {
  // Handle undefined or null
  if (!systemType) {
    return null;
  }
  
  // 1. Try exact match first (most explicit)
  if (SYSTEM_LAYOUTS[systemType]) {
    return SYSTEM_LAYOUTS[systemType];
  }
  
  // 2. Try normalized match (flexible matching)
  const normalizedType = systemType.toLowerCase().replace(/[_\s-]/g, '');
  
  for (const [key, layout] of Object.entries(SYSTEM_LAYOUTS)) {
    if (key.toLowerCase().replace(/[_\s-]/g, '') === normalizedType) {
      return layout;
    }
  }
  
  // 3. Pattern-based fallback for common variations
  if (normalizedType.includes('combi')) {
    return SYSTEM_LAYOUTS['combi'];
  } else if (normalizedType.includes('regular')) {
    return SYSTEM_LAYOUTS['regular'];
  } else if (normalizedType.includes('system')) {
    return SYSTEM_LAYOUTS['system'];
  } else if (normalizedType.includes('heatpump') || normalizedType.includes('hp') || normalizedType.includes('ashp')) {
    return SYSTEM_LAYOUTS['heat_pump'];
  }
  
  return null;
}
